import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@ludolio/shared-ui';
import { EmptyState, SectionTitle, TeamRoleBadge } from '@/components/common';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import type { LabTeamDetail } from '@/lib/types';

export const Route = createFileRoute('/$orgSlug/teams/$teamId')({
  component: TeamPage,
});

function TeamPage() {
  const { teamId } = Route.useParams();
  const labs = useLabs();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<LabTeamDetail | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'manager'>('member');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [transferUserId, setTransferUserId] = useState<string | null>(null);

  const backToTeams = useCallback(
    () => navigate({ to: '/$orgSlug/teams', params: { orgSlug: labs.organization.slug } }),
    [labs.organization.slug, navigate],
  );

  const apply = useCallback((next: LabTeamDetail) => {
    setDetail(next);
    setName(next.team.name);
    setDescription(next.team.description ?? '');
  }, []);

  useEffect(() => {
    let active = true;
    labsApi
      .getTeam(teamId)
      .then((next) => {
        if (active) apply(next);
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err, 'This team could not be loaded.'));
      });
    return () => {
      active = false;
    };
  }, [apply, teamId]);

  const run = async (action: () => Promise<LabTeamDetail | null>, success?: string) => {
    setBusy(true);
    try {
      const next = await action();
      if (!next) {
        backToTeams();
        return;
      }
      apply(next);
      if (success) toast.success(success);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !detail) {
    return <EmptyState title={error} action={<Button variant="outline" onClick={backToTeams}>Back to teams</Button>} />;
  }
  if (!detail) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const ownerCount = detail.members.filter((member) => member.role === 'owner').length;
  const transferTarget = detail.members.find((member) => member.userId === transferUserId);
  const detailsDirty = name.trim() !== detail.team.name || (description.trim() || null) !== detail.team.description;

  const saveDetails = () => run(
    () => labsApi.updateTeam(detail.team.id, { name: name.trim(), description: description.trim() || null }),
    'Team updated',
  );

  return (
    <div className="space-y-8">
      <Link
        to="/$orgSlug/teams"
        params={{ orgSlug: labs.organization.slug }}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Teams
      </Link>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="truncate text-2xl font-semibold tracking-tight">{detail.team.name}</h1>
        <TeamRoleBadge role={detail.role} />
      </div>

      {detail.permissions.canEditTeam ? (
        <form
          className="grid max-w-xl gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void saveDetails();
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="team-name">Name</Label>
            <Input id="team-name" value={name} maxLength={120} onChange={(event) => setName(event.target.value)} disabled={busy} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="team-description">Description</Label>
            <Textarea
              id="team-description"
              value={description}
              maxLength={2000}
              rows={3}
              onChange={(event) => setDescription(event.target.value)}
              disabled={busy}
            />
          </div>
          <div>
            <Button type="submit" disabled={busy || !detailsDirty || !name.trim()}>Save</Button>
          </div>
        </form>
      ) : (
        detail.team.description && <p className="max-w-2xl text-sm text-muted-foreground">{detail.team.description}</p>
      )}

      <section>
        <SectionTitle>Members</SectionTitle>
        <ul className="divide-y rounded-lg border">
          {detail.members.map((member) => {
            const isSelf = member.userId === labs.user.id;
            const lastOwner = member.role === 'owner' && ownerCount <= 1;
            const canRemove = !lastOwner && (
              isSelf
              || detail.role === 'owner'
              || (detail.role === 'manager' && member.role === 'member')
            );
            const canChangeRole = detail.permissions.canManageRoles && !isSelf && member.role !== 'owner';
            return (
              <li key={member.userId} className="grid items-center gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_9rem_auto]">
                <div className="min-w-0">
                  <p className="truncate font-medium">{member.name}{isSelf ? ' (you)' : ''}</p>
                  <p className="truncate text-sm text-muted-foreground">{member.email}</p>
                </div>
                {canChangeRole ? (
                  <Select
                    value={member.role}
                    onValueChange={(value) => {
                      if (value === member.role) return;
                      void run(() => labsApi.setTeamRole(detail.team.id, member.userId, value as 'manager' | 'member'));
                    }}
                    disabled={busy}
                  >
                    <SelectTrigger aria-label={`Role for ${member.name}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="sm:justify-self-start">
                    <TeamRoleBadge role={member.role} />
                  </div>
                )}
                <div className="flex items-center justify-end gap-2">
                  {detail.permissions.canTransferOwnership && member.role !== 'owner' && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => setTransferUserId(member.userId)}>
                      Make owner
                    </Button>
                  )}
                  {canRemove && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => void run(() => labsApi.removeTeamMember(detail.team.id, member.userId))}
                    >
                      {isSelf ? 'Leave' : 'Remove'}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {detail.permissions.canManageMembers && (
        <form
          className={
            detail.permissions.canManageRoles
              ? 'grid max-w-xl gap-3 sm:grid-cols-[minmax(0,1fr)_9rem_auto] sm:items-end'
              : 'flex flex-col gap-3 sm:flex-row sm:items-end'
          }
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              const next = await labsApi.addTeamMember(detail.team.id, email.trim(), inviteRole);
              setEmail('');
              return next;
            });
          }}
        >
          <div className="min-w-0 flex-1 space-y-2">
            <Label htmlFor="member-email">Add a member</Label>
            <Input
              id="member-email"
              type="email"
              value={email}
              placeholder="name@university.edu"
              onChange={(event) => setEmail(event.target.value)}
              disabled={busy}
            />
          </div>
          {detail.permissions.canManageRoles && (
            <div className="space-y-2">
              <Label htmlFor="member-role">Role</Label>
              <Select value={inviteRole} onValueChange={(value) => setInviteRole(value as 'member' | 'manager')} disabled={busy}>
                <SelectTrigger id="member-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <Button type="submit" disabled={busy || !email.trim()} className="sm:w-28">Add</Button>
        </form>
      )}

      <section>
        <SectionTitle>Projects</SectionTitle>
        {detail.projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">No projects yet</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {detail.projects.map((project) => (
              <li key={project.id}>
                <Link
                  to="/$orgSlug/projects/$projectId"
                  params={{ orgSlug: labs.organization.slug, projectId: project.id }}
                  className="block truncate p-4 font-medium hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {project.title}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AlertDialog open={Boolean(transferTarget)} onOpenChange={(open) => { if (!open) setTransferUserId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Transfer ownership</AlertDialogTitle>
            <AlertDialogDescription>
              {transferTarget?.name} will become an owner. You will become a manager.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !transferTarget}
              onClick={() => {
                if (!transferTarget) return;
                void run(
                  () => labsApi.transferTeamOwnership(detail.team.id, transferTarget.userId),
                  'Ownership transferred',
                );
              }}
            >
              Transfer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
